import i18next from 'i18next';
import { get, isNil, isNumber, isObject, isString } from 'lodash';
import { useTranslation } from 'react-i18next';
import strings from '@shared/util/text/translations/en.json';

// Type helper to extract all possible paths from a nested object
type Paths<T> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? T[K] extends object
          ? T[K] extends { zero?: string; one?: string; many?: string; max?: string }
            ? K | `${K}.${Paths<T[K]>}`
            : K | `${K}.${Paths<T[K]>}`
          : K
        : never;
    }[keyof T]
  : never;

// Type helper to get the value type at a path
type PathValue<T, P extends string> = P extends `${infer K}.${infer Rest}`
  ? K extends keyof T
    ? PathValue<T[K], Rest>
    : never
  : P extends keyof T
  ? T[P]
  : never;

// Type helper to extract parameter names from a template string
type ExtractParams<T extends string> = T extends `${infer _Start}{${infer Param}}${infer Rest}`
  ? Param extends `${infer Key}.${infer _SubKey}`
    ? Key | ExtractParams<Rest>
    : Param | ExtractParams<Rest>
  : never;

// Type helper to get all possible parameter names from a template value
type TemplateParams<T> = T extends string
  ? ExtractParams<T>
  : T extends { zero?: infer Z; one?: infer O; many?: infer M; max?: infer X }
  ? ExtractParams<
      | (Z extends string ? Z : never)
      | (O extends string ? O : never)
      | (M extends string ? M : never)
      | (X extends string ? X : never)
    >
  : never;

// Type helper to create a params object type from parameter names
type ParamsObject<Params extends string> = [Params] extends [never]
  ? Record<string, never>
  : Partial<Record<Params, string | number>>;

// Type for all valid i18n keys
export type I18nKey = Paths<typeof strings>;

// Type to get the params type for a specific key
type I18nParams<K extends I18nKey> = TemplateParams<PathValue<typeof strings, K>> extends never
  ? Record<string, unknown>
  : ParamsObject<TemplateParams<PathValue<typeof strings, K>>>;

function getActiveStrings(): typeof strings {
  const lang = i18next.language;
  const resources = i18next.store?.data;
  if (lang && resources) {
    const langShort = lang.split('-')[0];
    const translation = resources[lang]?.translation ?? resources[langShort]?.translation;
    if (translation) {
      return translation as typeof strings;
    }
  }
  return strings;
}

/*
  - Return a string template for a given key and params.
  - Templates are stored in shared/src/util/text/translations/en.json.
  - Templates may be objects with keys: zero, one, many, max.
  - If an object template is provided, the count and max params are used to
    determine which key to use.
  - e.g. { zero: "no one", one: "1 person", many: "{count} people", max: "a lot
      of people" }
*/
function getTemplate<K extends I18nKey>(key: K, params: I18nParams<K> = {}): string | undefined {
  const activeStrings = getActiveStrings();
  const tmpl = get(activeStrings, key) ?? get(strings, key);
  const count = params['count'];
  const max = params['max'];

  if (isNil(count) || typeof tmpl !== 'object') {
    return tmpl;
  }
  if (!isNil(tmpl.zero) && count === 0) {
    return tmpl.zero;
  }
  if (!isNil(tmpl.one) && count === 1) {
    return tmpl.one;
  }
  if (!isNil(tmpl.max) && isNumber(max) && isNumber(count) && count >= max) {
    return tmpl.max;
  }
  return tmpl.many;
}

export function i18n<K extends I18nKey>(key: K, params?: I18nParams<K>): string {
  const templateString = getTemplate(key, params);
  if (!templateString) {
    return `[STRING MISSING: "${key}"]`;
  }

  return interpolate(templateString, params);
}

export function interpolate(templateString: string, params: Record<string, unknown> = {}) {
  // interpolate template string with given params
  // https://stackoverflow.com/a/37650602/9117995
  return templateString.replace(/{([A-Za-z.]*)}/g, (_, key) => getFormattedValue(get(params, key)));
}

function getFormattedValue(value: unknown): string {
  if (typeof value === 'number') {
    return value.toLocaleString();
  } else {
    return String(value);
  }
}

export function isValidI18nKey(key?: string): key is I18nKey {
  if (isNil(key)) {
    return false;
  }
  const value = get(strings, key);
  return isString(value) || isPluralizationObject(value);
}

function isPluralizationObject(
  value: unknown,
): value is { zero?: string; one?: string; many?: string; max?: string } {
  return (
    isObject(value) && ('zero' in value || 'one' in value || 'many' in value || 'max' in value)
  );
}

/**
 * Hook version of `i18n()`. Subscribes the calling component to language
 * changes via react-i18next so it re-renders automatically when the user
 * switches language with `LanguageSwitcher`. Drop-in replacement for the
 * plain `i18n` function in any functional component.
 *
 * Usage:
 *   const t = useI18n();
 *   <Text>{t('menu.title')}</Text>
 */
export function useI18n(): typeof i18n {
  useTranslation(); // subscribe this component to languageChanged events
  return i18n;
}

export function getI18nKeyOrFallback(key: string | undefined, fallback: I18nKey): I18nKey {
  if (isValidI18nKey(key)) {
    return key;
  } else {
    return fallback;
  }
}
